import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { setUser } from "../../redux/slices/userSlice";
import { useNavigate } from "react-router-dom";
import {
  TextField,
  FormControl,
  InputAdornment,
  IconButton,
  Button,
  Typography,
  Box,
} from "@mui/material";
import logo from "../../Assets/logo-removebg.png";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";
import Banner from "../../components/Utils/Snackbar";
import {
  setPageLoadingTrue,
  setPageLoadingFalse,
  setCompanyId,
} from "../../redux/slices/appSlice";
import { post } from "../Utils/CallApi";
import "../CSS/Login.css";

const Login = () => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [usernameTouched, setUsernameTouched] = useState(false);
  const [passwordTouched, setPasswordTouched] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [showPassword, setShowPassword] = useState(false);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem("loginToken");
    if (token) {
      navigate("/dashboard");
    }
  }, [navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    dispatch(setPageLoadingTrue());
    try {
      const data = { username, password };
      const response = await post("user/generateToken", data);

      if (response.status === "success") {
        const token = response.result.token;
        const isSuperAdmin = response.result.groups.some(
          (group) => group.name === "wtcms_superAdmin"
        );
        const firstName = response.result.firstName;
        const email = response.result.email;

        setSnackbarMessage("Login successful! Redirecting...");
        setSnackbarSeverity("success");
        setSnackbarOpen(true);

        localStorage.setItem("loginToken", token);

        dispatch(setUser({ username, token, isSuperAdmin, firstName, email }));
        dispatch(setCompanyId(response.result.companyId));
        localStorage.setItem("companyId", response.result.companyId);
        dispatch(setPageLoadingFalse());

        navigate("/dashboard");
      } else {
        throw new Error(response.result || "Login failed. Please try again.");
      }
    } catch (error) {
      dispatch(setPageLoadingFalse());
      setSnackbarMessage(
        error.message || "Unexpected error occurred. Please contact support."
      );
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  };

  const isFormValid = username.length > 0 && password.length > 0;

  const openWhatsAppChat = () => {
    const phoneNumber = "+9613379747";
    const message = encodeURIComponent("Hi, I need help with logging in.");
    window.open(`https://wa.me/${phoneNumber}?text=${message}`, "_blank");
  };

  return (
    <div className="login-container">
      <div className="login-form">
        <img className="login-logo" alt="Logo" src={logo} />
        <form onSubmit={handleSubmit} style={{ marginTop: "-90px" }}>
          <TextField
            label="Username"
            fullWidth
            variant="outlined"
            value={username}
            onChange={(e) => {
              setUsername(e.target.value);
              setUsernameTouched(true);
            }}
            onBlur={() => setUsernameTouched(true)}
            error={usernameTouched && username.length <= 0}
            helperText={
              usernameTouched && username.length <= 0
                ? "Username is required"
                : ""
            }
            className="input-field"
          />
          <FormControl fullWidth variant="outlined" className="input-field">
            <TextField
              label="Password"
              type={showPassword ? "text" : "password"}
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
                setPasswordTouched(true);
              }}
              onBlur={() => setPasswordTouched(true)}
              error={passwordTouched && password.length <= 0}
              helperText={
                passwordTouched && password.length <= 0
                  ? "Password is required"
                  : ""
              }
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword((prev) => !prev)}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </FormControl>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            fullWidth
            disabled={!isFormValid}
            className="login-button"
          >
            Login
          </Button>
        </form>
        <Box display="flex" alignItems="center" justifyContent="center" mt={2}>
          <SupportAgentIcon sx={{ color: "#1E243E" }} />
          <Button
            onClick={openWhatsAppChat}
            sx={{ color: "#1E243E" }}
            size="small"
          >
            Need help?
          </Button>
        </Box>
      </div>
      <Banner
        open={snackbarOpen}
        message={snackbarMessage}
        severity={snackbarSeverity}
        handleClose={() => setSnackbarOpen(false)}
      />
    </div>
  );
};

export default Login;
